<template>
    <div>
        <validation-observer ref="createDirectorValidation">
            <validation-provider
                v-slot="{ errors }"
                name="id"
                rules="required"
            >
                <b-form-group >
                    <slot name="label">
                        <label id="id-row" for="id">
                            ID
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-popover target="id-row" triggers="hover" placement="right" variant="black">
                        Enter your NRIC or FIN if you are a Singapore resident.
                        <br>
                        Enter your passport number if you are a foreigner.
                    </b-popover>

                    <b-form-input
                        id='id'
                        v-model="director.nric"
                        placeholder="Enter NRIC or passport"
                        :state="errors.length > 0 ? false : null"
                        data-test="director-id"
                        @input="requestAutoFillDirectorFormData"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="director name"
                rules="required"
            >
                <b-form-group label="Name">
                    <b-form-input
                        id="name"
                        v-model="director.name"
                        placeholder="Enter director legal name"
                        :state="errors.length > 0 ? false : null"
                        data-test="director-name"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="ID type"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group
                    label="ID type"
                    :state="errors.length > 0 ? false : null"
                >
                    <v-select
                        ref="idType"
                        v-model="director.idType"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select ID type"
                        label="name"
                        :options="idType_options"
                        @input="handleIdTypeOnChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="nationality"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group label="Nationality"  :state="errors.length > 0 ? false : null">
                    <v-select
                        ref="nationality"
                        v-model="director.nationality"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select nationality"
                        label="text"
                        :disabled="shouldDisabledNationality"
                        :options="nationality_options"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <div v-if="shouldHideDirectorDateOfExpiry">
                <validation-provider
                    v-slot="{ errors }"
                    name="date of expiry"
                    rules="required"
                >
                    <b-form-group :state="errors.length > 0 ? false : null">
                        <slot name="label">
                            <label id="dateOfExpiry" for="id">
                                Date of expiry
                                <vector
                                    data-type="icon"
                                    data-width="15px"
                                    data-height="15px"
                                    data-class="mr-.5"
                                    data-name="information"
                                ></vector>
                            </label>
                        </slot>

                        <b-popover
                            target="dateOfExpiry"
                            triggers="hover"
                            placement="right"
                            variant="black"
                        >
                            Skip this if you are a Singapore citizen or permanent
                            resident.
                            <br />
                            Fill in the date of expiry of your passport or FIN if
                            you are a foreigner.
                        </b-popover>

                        <flat-pickr
                            v-model="director.dateOfExpiry"
                            placeholder="Enter date of expiry"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-doe"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>

            <validation-provider
                v-slot="{ errors }"
                name="date of birth"
                rules="required"
            >
                <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                    <flat-pickr
                        v-model="director.dob"
                        placeholder="Enter date of birth"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-of-birth"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="email address"
                rules="required|email"
            >
                <b-form-group label="Email address">
                    <b-form-input
                        v-model="director.email"
                        placeholder="Enter director email address"
                        :state="errors.length > 0 ? false : null"
                        data-test="director-email"
                        @change="onNominatorEmailChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="contact number"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="contactNumber" for="id">
                            Contact number
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-input
                        v-model="director.contactNumber"
                        placeholder="Enter director contact number"
                        :state="errors.length > 0 ? false : null"
                        data-test="director-contact-number"
                        type="tel"
                    />

                    <b-popover target="contactNumber" triggers="hover" placement="right" variant="black">
                        Enter your Singapore mobile number if you are a Singapore resident. 
                        <br>
                        Enter your overseas mobile number if you are a foreigner.
                    </b-popover>

                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="address"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="address" for="id">
                            Address
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-textarea
                        id="textarea"
                        v-model="director.address"
                        placeholder="Enter director address"
                        :state="errors.length > 0 ? false : null"
                        data-test="director-address"
                        rows="3"
                        max-rows="3"
                    ></b-form-textarea>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <b-popover target="address" triggers="hover" placement="right" variant="black">
                        Enter Singapore address if you are a Singapore resident. 
                        <br>
                        Enter your overseas address if you are a foreigner.
                    </b-popover>
                </b-form-group>
            </validation-provider>

            <!-- Nominee director nominator section -->
            <div class="d-flex justify-content-between align-items-center">
                <div class="is-nominee">
                    <validation-provider
                        name="nominee director"
                    >
                        <b-form-checkbox 
                            :value="true" 
                            :checked="director.isNomineeDirector" 
                            @change="handleDirectorPositionChange($event)"
                        >
                            Is nominee director
                        </b-form-checkbox>
                    </validation-provider>
                </div>

                <!-- nominator new or existing -->
                <div v-if="shouldShowNominatorFields" class="d-flex align-items-center mt-1">
                    <b-button
                        variant="outline-primary"
                        class="btn btn-sm d-flex justify-content-end align-items-center mb-1"
                        style="font-size: 1em;"
                        data-test="add-existing-nominator-btn"
                        :disabled="isExistingNominator"
                        @click="showNominatorField('existing', status)"
                    >
                        Existing Nominator
                        <vector
                            data-name="add-user"
                            data-width="15px"
                            data-height="12px"
                            class="ml-.3 d-flex justify-content-center align-items-center"
                        />
                    </b-button>

                    <b-button
                        variant="outline-primary"
                        class="btn btn-sm d-flex justify-content-end align-items-center mb-1 ml-1"
                        style="font-size: 1em;"
                        data-test="add-new-nominator-btn"
                        :disabled="!isExistingNominator"
                        @click="showNominatorField('new', status)"
                    >
                        New Nominator
                        <vector
                            data-name="add-user"
                            data-width="15px"
                            data-height="12px"
                            class=" ml-.3 d-flex justify-content-center align-items-center"
                        />
                    </b-button>
                </div>
                <!-- end nominator new or existing -->
            </div>

            <div v-if="shouldShowNominatorFields">
                <validation-provider
                    #default="{ errors }"
                    name="nominator type"
                    rules="required"
                    mode="lazy"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Nominator type"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="nominatorType"
                            v-model="director.nominatorType"
                            class="custom-select-icon text-capitalize"
                            :clearable="false"
                            placeholder="Select nominator type"
                            label="text"
                            :options="nominatorTypeOptions"
                            @input="onNominatorTypeChange"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    #default="{ errors }"
                    name="appointment date"
                    rules="required"
                >
                    <b-form-group label="Date on which the director became the nominee" :state="errors.length > 0 ? false : null">
                        <flat-pickr
                            v-model="director.appointmentDate"
                            placeholder="Pick a date"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-appointment-date"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    #default="{ errors }"
                    name="cessation date"
                >
                    <b-form-group label="Date on which the director ceased to be the nominee" :state="errors.length > 0 ? false : null">
                        <flat-pickr
                            v-model="director.cessationDate"
                            placeholder="Pick a date"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-cessation-date"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <div v-if="! isNewNominator">
                    <validation-provider
                        #default="{ errors }"
                        :name="dynamicNameLabel.toLowerCase()"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group
                            :label="dynamicNameLabel"
                            :state="errors.length > 0 ? false : null"
                        >
                            <v-select
                                id="name"
                                ref="nominator"
                                v-model="director.nominator"
                                :placeholder="dynamicNamePlaceholder"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-name"
                                class="custom-select-icon text-uppercase"
                                :clearable="false"
                                label="name"
                                :options="nominators"
                                @input="onNominatorNameChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <div v-if="isNewNominator">
                    <validation-provider
                        v-slot="{ errors }"
                        :name="dynamicIdLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group>
                            <slot name="label">
                                <label id="nominator-id" for="id">
                                    {{ dynamicIdLabel }}
                                </label>
                            </slot>

                            <b-form-input
                                id="id"
                                v-model="newNominator.nominatorNRIC"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="dynamicIdPlaceholder"
                                trim
                                data-test ="nric"
                                @input="requestAutoFillNominatorFormData"
                            />

                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <div v-if="isNominatorTypeIndividual">
                        <validation-provider
                            v-slot="{ errors }"
                            name="id type"
                            rules="required"
                        >
                            <b-form-group label="ID type" :state="errors.length > 0 ? false : null">
                                <v-select
                                    ref="idType"
                                    v-model="newNominator.nominatorIdType"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select ID type"
                                    label="name"
                                    :options="idType_options"
                                    @input="(idType) => handleIdTypeOnChange(idType, 'nominatorNationality')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="nationality"
                            rules="required"
                        >
                            <b-form-group  label="Nationality" :state="errors.length > 0 ? false : null" >
                                <v-select
                                    id="nationality"
                                    ref="nationality"
                                    v-model="newNominator.nominatorNationality"
                                    class="custom-select-icon"
                                    label="text" 
                                    placeholder="Select nationality"
                                    :clearable="false"
                                    :options="nationality_options"
                                    :disabled ="shouldDisabledNewNominatorNationality"
                                >
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <div v-if="shouldHideNominatorDateOfExpiry">
                            <validation-provider
                                v-slot="{ errors }"
                                name="nominator date of expiry"
                                rules="required"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null">
                                    <slot name="label">
                                        <label id="dateOfExpiry" for="id">
                                            Date of expiry
                                            <vector
                                                data-type="icon"
                                                data-width="15px"
                                                data-height="15px"
                                                data-class="mr-.5"
                                                data-name="information"
                                            ></vector>
                                        </label>
                                    </slot>

                                    <b-popover
                                        target="dateOfExpiry"
                                        triggers="hover"
                                        placement="right"
                                        variant="black"
                                    >
                                        Skip this if you are a Singapore citizen or permanent
                                        resident.
                                        <br />
                                        Fill in the date of expiry of your passport or FIN if
                                        you are a foreigner.
                                    </b-popover>

                                    <flat-pickr
                                        v-model="director.nominatorDateOfExpiry"
                                        placeholder="Enter date of expiry"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        data-test="date-picker-doe"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>
                    </div>

                    <validation-provider 
                        v-slot="{ errors }"
                        :name="dynamicNewNominatorNameLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group :label="dynamicNewNominatorNameLabel">
                            <b-form-input
                                id="name"
                                v-model="newNominator.newNominatorName"
                                :placeholder="dynamicNewNominatorPlaceholder"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-name"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <!-- < INDIVIDUAL NOMINATOR > -->
                <div v-if="isNominatorTypeIndividual">
                    <validation-provider
                        #default="{ errors }"
                        name="individual nominator aliases"
                    >
                        <b-form-group label="Individual nominator aliases, if any">
                            <b-form-input
                                id="name"
                                v-model="director.nominatorAlias"
                                placeholder="individual nominator aliases"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-alias"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>
                <!-- </ INDIVIDUAL NOMINATOR > -->

                <div v-if="isNewNominator">
                    <div v-if="isNominatorTypeIndividual">
                        <validation-provider
                            v-slot="{ errors }"
                            name="date of birth"
                            rules="required"
                        >
                            <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                                <flat-pickr
                                    v-model="newNominator.nominatorDOB"
                                    class="form-control"
                                    data-test="date-of-birth"
                                    placeholder="Enter date of birth"
                                    :config="datePickerConfig"
                                    :state="errors.length > 0 ? false : null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>
                    </div>

                    <validation-provider
                        v-slot="{ errors }"
                        :name="dynamicAddressLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group :label="dynamicAddressLabel">
                            <b-form-textarea
                                id="textarea"
                                v-model="newNominator.nominatorAddress"
                                :placeholder="dynamicAddressPlaceholder"
                                :state="errors.length > 0 ? false : null"
                                trim
                                rows="3"
                                max-rows="3"
                                data-test="address"
                            >
                            </b-form-textarea>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                </div>

                <!-- < CORPORATE NOMINATOR > -->
                <div v-if="! isNominatorTypeIndividual">
                    <validation-provider #default="{ errors }"
                        name="corporate nominator UEN"
                        rules="required"
                    >
                        <b-form-group label="Corporate nominator UEN issued by the registrar (ACRA)">
                            <b-form-input
                                id="name"
                                v-model="director.nominatorUen"
                                placeholder="Enter corporate nominator UEN"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-uen"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        #default="{ errors }"
                        name="corporate nominator legal form"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group
                            label="Corporate nominator legal form"
                            :state="errors.length > 0 ? false : null"
                        >
                            <v-select
                                ref="legalForm"
                                v-model="director.nominatorLegalForm"
                                placeholder="Select corporate nominator legal form"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="text"
                                :options="legalForms"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="corporate nominator jurisdiction"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Corporate nominator jurisdiction" :state="errors.length > 0 ? false : null">
                                <v-select
                                ref ="jurisdiction"
                                v-model="director.nominatorJurisdiction"
                                :clearable="false"
                                :options="jurisdictions"
                                label="name"
                                value="id"
                                class="custom-select-icon text-capitalize"
                                placeholder="Select corporate nominator jurisdiction"
                                @input="onNominatorJurisdictionChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="corporate nominator statue"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Corporate nominator statue under which the corporate is formed" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="statue"
                                v-model="director.nominatorStatue"
                                placeholder="Select corporate nominator statue"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="name"
                                value="id"
                                :options="statues"
                                :state="errors.length > 0 ? false : null"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="registrar"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group :state="errors.length > 0 ? false : null" label="Name of corporate nominator's register of the jurisdiction where the legal entity is formed or incorporated">
                            <v-select
                                ref="nominatorRegistrar"
                                v-model="director.nominatorRegistrar"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="name"
                                value="id"
                                :options="registrars"
                                :state="errors.length > 0 ? false : null"
                                placeholder="Select corporate registrar"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <validation-provider
                    #default="{ errors }"
                    name="remark"
                >
                    <b-form-group label="Remarks">
                        <b-form-input
                            id="remark"
                            v-model="director.remark"
                            placeholder="Enter nominee director remark"
                            :state="errors.length > 0 ? false : null"
                            data-test="remark"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <div class="switch-check mt-2 mb-2">
                    <b-form-checkbox 
                        v-model="director.generatePOA" 
                        :checked="director.generatePOA" 
                        switch
                        size="md"
                    > 
                        Generate POA Documents 
                    </b-form-checkbox>
                </div>

            </div>
            <!-- nominee director fields -->
        </validation-observer>

        <b-button
            block
            class="mt-2"
            variant="primary"
            data-test="create-director-btn"
            @click="createOrUpdate"
            >{{ status === "create" ? "Add" : "Update" }}
            <img
                v-if="status === 'create'"
                :src="'/illustrations/add-circle.svg'"
                class="float-right"
                alt="add-logo"
            />
            <img
                v-if="status === 'edit'"
                :src="'/illustrations/edit.svg'"
                class="float-right"
                alt="edit-logo"
            />
        </b-button>

        <b-button
            block
            class="mb-3"
            variant="outline-primary"
            data-test="cancelCreate"
            @click="$emit('cancel')"
            >Cancel
            <vector 
                data-type="icons" 
                data-width="15px" 
                data-height="15px" 
                data-name="cancel" 
                class="float-right"
                alt="cancel-logo"
            />
        </b-button>
    </div>
</template>

<script>
// eslint-disable-next-line vue/no-parsing-error
import { BFormGroup, BFormInput, BButton, BFormTextarea, BPopover, BFormCheckbox } from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { debounce } from 'debounce';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import IdType from "@/models/IdType";
import Nationality from '@/models/Nationality';
import Nominator from '@/models/Nominator';
import CompanyNomineeShareholder from '@/models/CompanyNomineeShareholder';
import User from '@/models/User';
import { NOMINEE_DIRECTOR, NRIC_CITIZEN_ID, SINGAPORE_CITIZEN } from "@/constants/config";
import { formatDate } from "@/helpers";

export default {
    components: {
        BFormGroup,
        BFormInput,
        BPopover,
        BButton,
        BFormTextarea,
        ValidationObserver,
        ValidationProvider,
        flatPickr,
        vSelect,
        BFormCheckbox,
    },

    props: {
        dataStatus: {
            required: false,
            default: "create",
            type: String,
        },

        dataDirector: {
            required: true,
            type: Object,
        },

        dataShareholders: {
            required: true,
            type: Array,
        },
    },

    data() {
        return {
            required,
            email,
            status: this.dataStatus,
            showNominatorFields: false,
            director: {
                // eslint-disable-next-line no-underscore-dangle
                _id: this.dataDirector._id,
                name: this.dataDirector.name,
                idType: this.dataDirector?.idType,
                nric: this.dataDirector.nric,
                nationality: this.dataDirector.nationality,
                dateOfExpiry: this.dataDirector.dateOfExpiry,
                dob: this.dataDirector.dob,
                email: this.dataDirector.email,
                contactNumber: this.dataDirector.contactNumber,
                address: this.dataDirector.address,
                remark: this.dataDirector.remark,
                isNomineeDirector: this.dataDirector.isNomineeDirector,

                // nominee director fields
                appointmentDate: this.dataDirector.appointmentDate,
                cessationDate: this.dataDirector.cessationDate,
                nominatorType: this.dataDirector.nominatorType,
                nominator: this.dataDirector.nominator,
                newNominatorName: this.dataDirector.newNominatorName,
                nominatorAlias: this.dataDirector.nominatorAlias,
                nominatorEmail: this.dataDirector.nominatorEmail,

                nominatorUen: this.dataDirector.nominatorUen,
                nominatorStatue: this.dataDirector.nominatorStatue,
                nominatorLegalForm: this.dataDirector.nominatorLegalForm,
                nominatorJurisdiction: this.dataDirector.nominatorJurisdiction,
                nominatorRegistrar: this.dataDirector.nominatorRegistrar,

                generatePOA: this.dataDirector.generatePOA,
            },

            newNominator : {
                nominatorNRIC: undefined,
                newNominatorName: undefined,
                nominatorIdType: undefined,
                nominatorNationality: undefined,
                nominatorDOB: undefined,
                nominatorAddress: undefined,
                nominatorDateOfExpiry: undefined,
            },

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            idType_options: [],
            nationality_options: [],

            // nominee director
            nominatorTypeOptions: [],

            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            nominators: [],
            jurisdictions: [],

            isExistingNominator : true,
            isNewNominator : false,
        };
    },

    computed: {
        shouldDisabledNationality() {
            return this.director?.idType?.id === NRIC_CITIZEN_ID;
        },

        shouldDisabledNewNominatorNationality() {
            return this.newNominator?.nominatorIdType?.id === NRIC_CITIZEN_ID;
        },

        shouldShowNominatorFields() {
            if(this.director.isNomineeDirector) 
                return true;
            return false;
        },  

        shouldHideDirectorDateOfExpiry() {
            if(this.director.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.director.dateOfExpiry = null;
            }
            return ! this.director.idType?.name.includes("NRIC");
        },

        shouldHideNominatorDateOfExpiry() {
            if(this.newNominator?.nominatorIdType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.newNominator.nominatorDateOfExpiry = null;
            }

            return ! this.newNominator?.nominatorIdType?.name.includes("NRIC");
        },

        isNominatorTypeIndividual() {
            // if there is no type yet, act like it's Individual
            if (! this.director.nominatorType) return true;

            return (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL
            );
        },

        dynamicIdLabel() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator id on the corporate entity register of the jurisdiction where the corporate controller is formed or incorporated";

            return "Individual nominator ID";
        },

        dynamicIdPlaceholder() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator id";

            return "Enter individual nominator id";
        },

        dynamicNameLabel() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Individual nominator name";
        },

        dynamicNamePlaceholder() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Select corporate nominator";

            return "Select individual nominator";
        },

        // new nominator fields
        dynamicNewNominatorNameLabel() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Name";
        },

        dynamicNewNominatorPlaceholder() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator name";

            return "Enter nominator name";
        },

        dynamicAddressLabel() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text ===
                    NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Address of registered office";

            return "Address";
        },

        dynamicAddressPlaceholder() {
            if (
                this.director.nominatorType &&
                this.director.nominatorType.text ===
                    NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter address of registered office";

            return "Enter address";
        },
    },
    
    async created() {
        this.shouldShowNewNominatorFields(this.dataDirector, this.dataShareholders);

        const data = await Nationality.all();
        this.idType_options = await IdType.all();

        this.nationality_options = data.map(obj => ({
            value: obj.id,
            text: obj.name
        }));

        // if id type is NRIC citizen and nationality value not set, it should be singapore citizen
        this.handleIdTypeOnChange(this.director?.idType); 

        if(this.director)
            this.reInitializeNominators(this.director.nominatorType ? this.director.nominatorType.text : NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL);

        const payload = await Nominator.getCorporateData();
        
        this.jurisdictions = payload.jurisdiction;
        this.statues = payload.statue;
        this.dataStatues = this.statues;
        this.legalForms = payload.legal_form;
        this.registrars = payload.registrar;
        this.dataRegistrars = this.registrars;

        // get nominator type options
        const nominatorTypeOptionsData = await CompanyNomineeShareholder.getNominatorTypeOptions();
        this.nominatorTypeOptions = nominatorTypeOptionsData.map(opt => {
            const text  = opt;
            const value  = opt;
            return { text, value };
        });
    },

    methods: {
        handleIdTypeOnChange(data, nationalityKey = 'nationality')
        {
            if(data && data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationality_options.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                if(nationalityKey === 'nominatorNationality')
                    this.newNominator[nationalityKey] = singaporeCitizen;
                
                else this.director[nationalityKey] = singaporeCitizen;
            }
        },

        onNominatorNameChange(nominator) {
            this.requestAutoFillNominatorFormData(nominator.nric);
        },

        // eslint-disable-next-line func-names
        requestAutoFillDirectorFormData: debounce(async function(id) {
            if(id) {
                const user = await User.getByNRIC(id);

                if(user) this.fillDirectorData(user);
            }
        }, 500),

        // eslint-disable-next-line func-names
        requestAutoFillNominatorFormData: debounce(async function(id) {
            const type = this.director?.nominatorType?.text;

            if(type && id) {
                const user = await User.getByNRIC(id, type);

                if(! user) this.director.nominatorEmail = undefined;

                if(user) this.fillNominatorData(user);
            }
        }, 500),

        fillDirectorData(user) {
            this.director.name = user?.user_info?.legal_name ?? user.name;
            this.director.idType = user.user_info?.id_type
                ? {
                    id: user.user_info.id_type.id,
                    name: user.user_info.id_type.name
                }
                : null;
            this.director.nric = user.user_info?.NRIC;
            this.director.nationality = user.user_info?.nationality
                ? { 
                    value: user.user_info.nationality.id,
                    text: user.user_info.nationality.name 
                }
                : null;
            this.director.dateOfExpiry = formatDate(user.user_info?.date_of_expiry);
            this.director.dob = formatDate(user.user_info?.DOB);
            this.director.email = user.email;
            this.director.contactNumber = user.user_info?.contact_number;
            this.director.address = user.user_info?.address;
        },

        fillNominatorData(nominator) {
            if(this.isNewNominator) {
                if (nominator.user_info?.id_type) {
                    this.newNominator.nominatorIdType = {
                        id: nominator.user_info.id_type?.id,
                        name: nominator.user_info.id_type?.name
                    };
                }
                if (nominator.user_info?.nationality) {
                    this.newNominator.nominatorNationality = {
                        text: nominator.user_info.nationality?.name,
                        value: nominator.user_info.nationality?.id
                    };
                }
                this.newNominator.nominatorDateOfExpiry = formatDate(nominator?.user_info?.date_of_expiry);

                this.newNominator.nominatorNRIC = nominator.user_info?.NRIC;
                this.newNominator.newNominatorName = nominator?.user_info?.legal_name ?? nominator?.name;
                
                // just sure backend not generate email for new nominator
                this.director.nominatorEmail = nominator?.email;
                
                this.newNominator.nominatorDOB = formatDate(nominator?.user_info?.DOB);
                this.newNominator.nominatorAddress = nominator?.user_info?.address;

                if
                (
                    ! nominator?.user_info?.nationality 
                    && nominator?.user_info?.id_type?.name === "NRIC (Citizen)"
                )
                {
                    this.newNominator.nominatorNationality = { value: 169, text: "SINGAPORE CITIZEN" };
                }
            }

            if(this.isNominatorTypeIndividual) 
                this.director.nominatorAlias = nominator?.individual_shareholder?.alias;

            if(! this.isNominatorTypeIndividual) {
                this.director.nominatorUen = nominator?.user_info?.uen;
                this.director.nominatorStatue = nominator?.shareholder?.statue;
                this.director.nominatorRegistrar = nominator?.shareholder?.registrar;
                this.director.nominatorJurisdiction = nominator?.shareholder?.jurisdiction;
                this.director.nominatorLegalForm = nominator?.shareholder?.legal_form;
            }
        },

        // nominee director region
        reInitializeNominators(type)
        {
            if(this.director.email) {
                this.nominators = this.dataShareholders.filter(shareholder => 
                    shareholder.shareholderType.text === type &&
                        this.director.email.toLowerCase() !== shareholder.email.toLowerCase()
                ).map(shareholder => ({
                    referenceId: shareholder.referenceId,
                    userId: shareholder.userId,
                    name: shareholder.name,
                    email: shareholder.email,
                    nric: shareholder.nric
                }));
            }
            else {
                this.nominators = this.dataShareholders.filter(shareholder => shareholder.shareholderType.text === type).map(shareholder => ({
                    referenceId: shareholder.referenceId,
                    userId: shareholder.userId,
                    name: shareholder.name,
                    email: shareholder.email,
                    nric: shareholder.nric
                }));
            }
        },

        onNominatorEmailChange(directorEmail) {
            const sameNominator = this.dataShareholders.filter(shareholder => 
                directorEmail.toLowerCase() === shareholder.email.toLowerCase()
            ).length > 0;

            if(sameNominator)
                this.director.nominator = undefined;

            this.reInitializeNominators(this.director.nominatorType?.text);
        },

        handleDirectorPositionChange(event) {
            this.director.isNomineeDirector = event;
        },

        async onNominatorTypeChange(payload)
        {
            this.reInitializeNominators(payload.value);
            this.director.nominator = '';
            this.director.nominatorUen = null;
        },

        onNominatorJurisdictionChange(payload)
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === payload.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === payload.id);
            this.director.nominatorStatue = '';
            this.director.nominatorRegistrar = '';
        },

        showNominatorField (value) {
            if(value === 'new') {
                this.isExistingNominator= false;
                this.isNewNominator= true;
            }

            if(value === 'existing') {
                this.isExistingNominator = true;
                this.isNewNominator = false;
            }
        },

        shouldShowNewNominatorFields(director, shareholders) {
            if(director.isNewNominator && !director.nominatorEmail) {
                this.initializeAndShowNewNominatorData(director);
            };
            
            if(director.nominatorEmail) {
                const notHas = shareholders.filter(shareholder => director.nominatorEmail === shareholder.email).length < 1;

                if(notHas)
                    this.initializeAndShowNewNominatorData(director);
            }
        },

        initializeAndShowNewNominatorData(director) {
            this.newNominator.nominatorNRIC = director.nominatorNRIC;
            this.newNominator.newNominatorName = director.newNominatorName;
            this.newNominator.nominatorIdType = director.nominatorIdType;
            this.newNominator.nominatorNationality = director.nominatorNationality;
            this.newNominator.nominatorDOB = director.nominatorDOB;
            this.newNominator.nominatorAddress = director.nominatorAddress;

            this.isNewNominator = true;
            this.isExistingNominator = false;
        },

        /* eslint-disable no-param-reassign */
        assignIfNewNominator(director) {
            if(this.isNewNominator) {
                director.nominatorNRIC = this.newNominator.nominatorNRIC;
                director.newNominatorName = this.newNominator.newNominatorName;
                director.nominatorIdType = this.newNominator.nominatorIdType;
                director.nominatorNationality = this.newNominator.nominatorNationality;
                director.nominatorDOB = this.newNominator.nominatorDOB;
                director.nominatorAddress = this.newNominator.nominatorAddress;
            }

            director.isNewNominator = this.isNewNominator;
            return director;
        },
        /* eslint-enable no-param-reassign */
        // end nominee director region

        createOrUpdate() {
            this.$refs.createDirectorValidation
                .validate()
                .then(async success => {
                    if (success) {
                        this.$emit('createOrEdit', this.assignIfNewNominator(this.director));
                        this.$emit("cancel");
                    }
                });
        },
    },
};
</script>